import { useEffect, useMemo, useState } from 'react';
import { Search, Loader2, User, ArrowRight } from 'lucide-react';
import { visitReportsApi } from '../../services/visitReportsApi';
import type {
    VisitReportSalesperson as Salesperson,
    VisitReportCustomerOption as CustomerPortfolioItem,
} from '../../types/visitReport';

interface MobileCustomerPickerProps {
    onSelect: (customer: CustomerPortfolioItem) => void;
}

export function MobileCustomerPicker({ onSelect }: MobileCustomerPickerProps) {
    const [salespeople, setSalespeople] = useState<Salesperson[]>([]);
    const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
    const [customers, setCustomers] = useState<CustomerPortfolioItem[]>([]);
    const [loadingSalespeople, setLoadingSalespeople] = useState(true);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [search, setSearch] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoadingSalespeople(true);
        visitReportsApi.getSalespeople()
            .then(data => {
                if (cancelled) return;
                setSalespeople(data);
                if (data.length > 0) setSelectedStaffId(data[0].staffId);
            })
            .catch(err => { if (!cancelled) setError(err.message); })
            .finally(() => { if (!cancelled) setLoadingSalespeople(false); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!selectedStaffId) return;
        let cancelled = false;
        setLoadingCustomers(true);
        setError(null);
        visitReportsApi.getCustomers(selectedStaffId)
            .then(data => { if (!cancelled) setCustomers(data); })
            .catch(err => { if (!cancelled) setError(err.message); })
            .finally(() => { if (!cancelled) setLoadingCustomers(false); });
        return () => { cancelled = true; };
    }, [selectedStaffId]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return customers.slice(0, 100);
        return customers
            .filter(c =>
                c.customerName.toLowerCase().includes(q) ||
                (c.city?.toLowerCase().includes(q) ?? false) ||
                c.customerId.toLowerCase().includes(q)
            )
            .slice(0, 100);
    }, [search, customers]);

    return (
        <div className="space-y-3">
            {loadingSalespeople ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading salespeople …
                </div>
            ) : (
                <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                    <User className="w-4 h-4 text-gray-400" />
                    <select
                        value={selectedStaffId ?? ''}
                        onChange={e => setSelectedStaffId(e.target.value)}
                        className="flex-1 text-sm bg-transparent focus:outline-none cursor-pointer"
                    >
                        {salespeople.map(s => (
                            <option key={s.staffId} value={s.staffId}>
                                {s.fullName} ({s.companyCode.toUpperCase()})
                            </option>
                        ))}
                    </select>
                </label>
            )}

            <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                <Search className="w-4 h-4 text-gray-400" />
                <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search customer …"
                    className="flex-1 text-sm bg-transparent focus:outline-none"
                />
            </label>

            {error && (
                <div className="text-sm text-red-600">{error}</div>
            )}

            <div className="space-y-1 max-h-[50vh] overflow-y-auto">
                {loadingCustomers ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 p-4 justify-center">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading customers …
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-sm text-gray-500 py-6 text-center">No customers found.</div>
                ) : filtered.map(c => (
                    <button
                        key={`${c.customerId}-${c.companyCode}`}
                        type="button"
                        onClick={() => onSelect(c)}
                        className="w-full text-left flex items-center justify-between gap-2 px-3 py-3 bg-white border border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 active:scale-[0.99] transition"
                    >
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-800 truncate">{c.customerName}</div>
                            <div className="text-xs text-gray-500 truncate">
                                {c.companyCode.toUpperCase()} · {c.city || '—'}{c.countryISO3 ? `, ${c.countryISO3}` : ''}
                            </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </button>
                ))}
            </div>
        </div>
    );
}
